import { ContactDefaults } from './ContactDefaults.js';
import { useId, useMemo, useState, type ReactNode } from 'react';
import {
  useApproveTagMutation, useCrmTagsQuery, useKeapTagsQuery, useMeQuery,
  useSaveTagMappingMutation, useTagMappingsQuery, useUpdateRelationshipTypeMutation,
  useWithdrawTagMutation,
  type CrmTagOption, type KeapTag,
} from './api.js';
import { Alert, Button, Card, SectionHeading, Spinner } from './ui.js';
import { STAGES, keyForTag } from './crmTagKey.js';
import { stageNote } from './crmTagSelection.js';

/**
 * Settings → CRM Tags: the CRM tags ContactPilot can use when capturing contacts.
 *
 * One list, whatever each tag is underneath. The three stage tags are shown as ordinary
 * tags with a note that only one of them can be on a contact; the generic tags are added
 * from the CRM's own list and removed again; a legacy relationship type that carries a
 * tag appears like any other tag and is archived when removed. Where a contact came from
 * is not a tag at all — the capture records it — so the CRM tag that used to mark business
 * cards can be added here like any other.
 *
 * Nothing on this screen changes anything in the CRM. Every control records which
 * EXISTING CRM tag ContactPilot may use; no tag is created and no contact is tagged.
 */

type Result = { tone: 'success' | 'error'; text: string } | null;

const WRAP = '[overflow-wrap:anywhere]';
const FOCUS = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900';
const SELECT = 'mt-1 min-h-11 w-full min-w-0 max-w-md rounded-md border border-slate-300 bg-white px-2 text-base disabled:bg-slate-50 sm:text-sm';

const isAdmin = (role: string | null | undefined) => role === 'owner' || role === 'admin';

function errorText(e: unknown, fallback: string): string {
  const status = (e as { status?: number })?.status;
  const data = (e as { data?: { error?: string } })?.data;
  if (status === 403) return 'Only a workspace owner or admin can change this.';
  if (data?.error === 'unknown_tag') return 'That tag no longer exists in your CRM. Reload and choose again.';
  return fallback;
}

/** One live CRM tag, chosen from a native select. Long names are the select's problem. */
function TagSelect({ id, label, value, tags, onChange, disabled }: {
  id: string; label: ReactNode; value: string; tags: readonly KeapTag[];
  onChange: (externalId: string) => void; disabled?: boolean;
}) {
  const known = value === '' || tags.some((t) => t.externalId === value);
  return (
    <div className="min-w-0 text-sm">
      <label htmlFor={id} className="text-slate-600">{label}</label>
      <select
        id={id} value={value} disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={`${SELECT} ${FOCUS}`}
      >
        {value === '' ? <option value="" disabled>Choose a CRM tag</option> : null}
        {!known ? <option value={value}>Current tag (not in your CRM list)</option> : null}
        {tags.map((t) => <option key={t.externalId} value={t.externalId}>{t.name}</option>)}
      </select>
    </div>
  );
}

/** A tag's name, wrapping, with its full value reachable. */
function TagName({ name }: { name: string }) {
  return <span className={`min-w-0 text-sm font-medium text-slate-900 ${WRAP}`} title={name}>{name}</span>;
}

const rowCls = 'min-w-0 px-3 py-2';
const actionCls = 'min-h-11 shrink-0';

/**
 * A stage tag: "Signal - Prospect ✓", with the CRM tag behind it one "Change mapping" away.
 *
 * Set once, at setup. A page that opened on three live selects would read as three
 * decisions still to make, so the default is the answer and the picker is a step away.
 */
function StageRow({ stage, tag, tags, tagsReady, canEdit, onResult }: {
  stage: string; tag: CrmTagOption | null; tags: readonly KeapTag[]; tagsReady: boolean;
  canEdit: boolean; onResult: (r: Result) => void;
}) {
  const id = useId();
  const [editing, setEditing] = useState(tag === null);
  const [save, { isLoading }] = useSaveTagMappingMutation();

  const change = async (externalId: string) => {
    const t = tags.find((x) => x.externalId === externalId);
    if (!t) return;
    onResult(null);
    try {
      await save({ canonicalKey: stage, externalTagId: t.externalId, displayName: t.name }).unwrap();
      setEditing(false);
      onResult({ tone: 'success', text: `“${t.name}” is now available. Nothing was changed in your CRM.` });
    } catch (e) {
      onResult({ tone: 'error', text: errorText(e, 'That tag could not be saved.') });
    }
  };

  return (
    <li className={rowCls} data-crm-row="" data-stage-tag="">
      <div className="flex min-w-0 items-center justify-between gap-3">
        {tag ? (
          <p className="flex min-w-0 items-baseline gap-1.5">
            <TagName name={tag.label} />
            <span className="shrink-0 text-emerald-700" aria-hidden="true">✓</span>
            <span className="sr-only">(chosen)</span>
          </p>
        ) : (
          <p className="min-w-0 text-sm text-amber-900">
            Choose a CRM tag for {stage === 'Existing Customer' ? 'existing customers' : `${stage.toLowerCase()}s`}.
          </p>
        )}
        {tag ? (
          <Button
            variant="secondary" className={actionCls} disabled={!canEdit && !editing}
            onClick={() => setEditing((v) => !v)}
          >
            {editing ? 'Done' : 'Change mapping'}<span className="sr-only"> for {tag.label}</span>
          </Button>
        ) : null}
      </div>
      {editing ? (
        <div className="mt-2">
          <TagSelect
            id={id}
            label={tag ? <>CRM tag<span className="sr-only"> instead of {tag.label}</span></> : 'CRM tag'}
            value={tag?.externalTagId ?? ''}
            tags={tags}
            disabled={!canEdit || !tagsReady || isLoading}
            onChange={(v) => void change(v)}
          />
        </div>
      ) : null}
    </li>
  );
}

/** A generic tag, or a legacy relationship type that carries one. Removing never touches the CRM. */
function TagRow({ tag, canEdit, onResult }: { tag: CrmTagOption; canEdit: boolean; onResult: (r: Result) => void }) {
  const [withdraw, withdrawState] = useWithdrawTagMutation();
  const [archive, archiveState] = useUpdateRelationshipTypeMutation();
  const busy = withdrawState.isLoading || archiveState.isLoading;

  const remove = async () => {
    onResult(null);
    try {
      if (tag.kind === 'type') await archive({ key: tag.key, enabled: false }).unwrap();
      else await withdraw(tag.key).unwrap();
      onResult({ tone: 'success', text: `Removed “${tag.label}”. Contacts that already have it keep it.` });
    } catch (e) {
      onResult({ tone: 'error', text: errorText(e, 'That tag could not be removed.') });
    }
  };

  return (
    <li className={`${rowCls} flex items-center justify-between gap-3`} data-crm-tag="">
      <TagName name={tag.label} />
      <Button variant="secondary" className={actionCls} disabled={!canEdit || busy} onClick={() => void remove()}>
        Remove<span className="sr-only"> {tag.label}</span>
      </Button>
    </li>
  );
}

/** "+ Add CRM tag": a searchable list of the CRM's own tags, minus what is already used. */
function AddTag({ available, existing, canEdit, onResult, onDone }: {
  available: readonly KeapTag[];
  existing: ReadonlyArray<{ canonicalKey: string; externalTagId: string | null }>;
  canEdit: boolean; onResult: (r: Result) => void; onDone: () => void;
}) {
  const [approve, approveState] = useApproveTagMutation();
  const [filter, setFilter] = useState('');
  const searchId = useId();
  const needle = filter.trim().toLowerCase();
  const shown = needle ? available.filter((t) => t.name.toLowerCase().includes(needle)) : available;

  const add = async (tag: KeapTag) => {
    onResult(null);
    try {
      await approve({
        canonicalKey: keyForTag(tag, existing),
        externalTagId: tag.externalId,
        displayName: tag.name,
        category: 'other',
        // Applied by label mappings, defaults or a reviewer. Never offered to a model.
        aiAllowed: false,
        autoApplyAllowed: false,
        reviewRequired: true,
      }).unwrap();
      onResult({ tone: 'success', text: `Added “${tag.name}”. Nothing was changed in your CRM.` });
    } catch (e) {
      onResult({ tone: 'error', text: errorText(e, `“${tag.name}” could not be added.`) });
    }
  };

  return (
    <div className="min-w-0 space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3" data-tag-picker="">
      <label htmlFor={searchId} className="block text-sm text-slate-700">Search your CRM tags</label>
      <input
        id={searchId} type="search" value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder={`Filter ${available.length} tag${available.length === 1 ? '' : 's'}`}
        className={`min-h-11 w-full min-w-0 max-w-md rounded-md border border-slate-300 bg-white px-3 text-base sm:text-sm ${FOCUS}`}
      />
      {available.length === 0 ? (
        <p className="text-sm text-slate-600">Every tag in your CRM is already available here.</p>
      ) : shown.length === 0 ? (
        <p className={`text-sm text-slate-600 ${WRAP}`}>No tag matches “{filter}”.</p>
      ) : (
        <ul className="max-h-72 min-w-0 overflow-y-auto rounded-md border border-slate-200 bg-white" aria-label="Tags in your CRM">
          {shown.map((t) => (
            <li key={t.externalId} className="border-b border-slate-100 last:border-b-0">
              <button
                type="button"
                disabled={!canEdit || approveState.isLoading}
                onClick={() => void add(t)}
                title={t.name}
                className={`flex min-h-11 w-full min-w-0 items-center px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 disabled:opacity-50 ${WRAP} ${FOCUS}`}
              >
                {t.name}
              </button>
            </li>
          ))}
        </ul>
      )}
      <Button variant="secondary" className="min-h-11" onClick={onDone}>Done</Button>
    </div>
  );
}

export function CrmSettings({ onOpenConnections }: { onOpenConnections?: () => void }) {
  const me = useMeQuery();
  const vocabulary = useCrmTagsQuery();
  const mappings = useTagMappingsQuery();
  const keap = useKeapTagsQuery();
  const [result, setResult] = useState<Result>(null);
  const [adding, setAdding] = useState(false);

  const live = useMemo(
    () => [...(keap.data?.tags ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [keap.data],
  );

  if (vocabulary.isLoading || mappings.isLoading) return <Spinner label="Loading CRM tags…" />;
  if (vocabulary.error || !vocabulary.data || !mappings.data) {
    return <Alert tone="error">CRM tags could not be loaded.</Alert>;
  }

  const canEdit = isAdmin(me.data?.role);
  const tagsReady = Boolean(keap.data) && !keap.error;
  const vocab = vocabulary.data;
  const core = mappings.data.mappings;
  const approved = mappings.data.approvedTags ?? [];

  const stageTags = STAGES.map((k) => ({ key: k, tag: vocab.tags.find((t) => t.kind === 'stage' && t.key === k) ?? null }))
    .filter((s) => s.tag !== null || vocab.unmappedStages.includes(s.key));
  const others = vocab.tags.filter((t) => t.kind !== 'stage');
  const note = stageNote(vocab);

  /*
   * Tags already used for something, so never offered to add: every tag already in the
   * list, the stage tags (even one switched off), and any relationship-type tag. The CRM
   * tag that once marked business cards is NOT reserved: provenance is no longer a tag, so
   * a customer who wants that tag on contacts adds it here and chooses it like any other.
   */
  const reserved = new Set<string>([
    ...vocab.tags.map((t) => t.externalTagId).filter((x): x is string => Boolean(x)),
    ...core.filter((m) => (STAGES as readonly string[]).includes(m.canonicalKey))
      .map((m) => m.externalTagId).filter((x): x is string => Boolean(x)),
    ...approved.filter((a) => a.category === 'relationship_type' || a.category === 'relationship')
      .map((a) => a.externalTagId).filter((x): x is string => Boolean(x)),
  ]);
  const available = live.filter((t) => !reserved.has(t.externalId));

  return (
    <div className="min-w-0 space-y-6">
      <SectionHeading
        title="CRM Tags"
        hint="Choose the CRM tags ContactPilot can use when capturing contacts."
      />

      <ContactDefaults />

      {!canEdit ? (
        <Alert tone="info">Only a workspace owner or admin can change these settings. You can see them here.</Alert>
      ) : null}
      {keap.isLoading ? <Spinner label="Reading tags from your CRM…" /> : null}
      {!keap.isLoading && !tagsReady ? (
        <Alert tone="error">
          <span className="block">Your CRM tags could not be read, so there is nothing to choose from. Check the Keap connection.</span>
          {onOpenConnections ? (
            <button
              type="button" onClick={onOpenConnections}
              className={`mt-1 inline-flex min-h-11 items-center font-medium underline ${FOCUS}`}
            >
              Go to Connections
            </button>
          ) : null}
        </Alert>
      ) : null}

      <Card className="p-5">
        <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-slate-900">Which CRM tags should ContactPilot let me use?</h3>
            <p className="mt-1 text-sm text-slate-600">
              Every tag picker uses this list. ContactPilot does not pick them on its own.
            </p>
          </div>
          {!adding ? (
            <Button
              variant="secondary" className="min-h-11 shrink-0 self-start" disabled={!canEdit || !tagsReady}
              onClick={() => { setResult(null); setAdding(true); }}
            >
              + Add CRM tag
            </Button>
          ) : null}
        </div>

        {adding ? (
          <div className="mt-3">
            <AddTag
              available={available} existing={approved} canEdit={canEdit}
              onResult={setResult} onDone={() => setAdding(false)}
            />
          </div>
        ) : null}

        <ul className="mt-4 min-w-0 divide-y divide-slate-100 rounded-md border border-slate-200" aria-label="Stage CRM tags" data-stage-mappings="">
          {stageTags.map((s) => (
            <StageRow
              key={s.key} stage={s.key} tag={s.tag} tags={live} tagsReady={tagsReady}
              canEdit={canEdit} onResult={setResult}
            />
          ))}
        </ul>
        {note ? <p className="mt-1.5 text-xs text-slate-500">{note}</p> : null}

        {others.length > 0 ? (
          <ul className="mt-3 min-w-0 divide-y divide-slate-100 rounded-md border border-slate-200" aria-label="Other CRM tags">
            {others.map((t) => <TagRow key={t.id} tag={t} canEdit={canEdit} onResult={setResult} />)}
          </ul>
        ) : (
          <p className="mt-3 text-sm text-slate-500">No other tags yet. Add the CRM tags you want on contacts.</p>
        )}

        <div aria-live="polite" className="mt-3">{result ? <Alert tone={result.tone}>{result.text}</Alert> : null}</div>
      </Card>
    </div>
  );
}
